import { useEffect, useReducer } from "react";
import { HeaderAction, HeaderState } from "../types";

// Initial state for the header
const initialHeaderState: HeaderState = {
	components: [
		{
			id: "logo-1",
			type: "logo",
			settings: {
				size: "M",
				image: "https://placehold.co/80x30?text=Logo",
				link: "/",
			},
			styles: {
				padding: "8px",
			},
		},
		{
			id: "search-1",
			type: "searchBar",
			settings: {
				size: "full",
				placeholder: "Search...",
				icon: "search",
			},
			styles: {
				width: "250px",
			},
		},
		{
			id: "wishlist-1",
			type: "wishlist",
			settings: {
				size: "M",
				type: "button",
				icon: "heart",
				shape: "rounded", // Added shape setting
				hoverAnimation: "none", // Added hover animation
			},
			styles: {},
		},
		{
			id: "cart-1",
			type: "cart",
			settings: {
				size: "M",
				type: "button",
				icon: "cart",
				shape: "rounded", // Added shape setting
				hoverAnimation: "none", // Added hover animation
			},
			styles: {},
		},
		{
			id: "user-1",
			type: "user",
			settings: {
				size: "M",
				type: "button",
				icon: "user",
				shape: "rounded", // Added shape setting
				hoverAnimation: "none", // Added hover animation
			},
			styles: {},
		},
		{
			id: "menu-1",
			type: "menu",
			settings: {
				size: "M",
				type: "button",
				icon: "menu",
				shape: "rounded", // Added shape setting
				hoverAnimation: "none", // Added hover animation
			},
			styles: {},
		},
		{
			id: "nav-1",
			type: "navItems",
			settings: {
				items: [
					{ id: "nav-item-1", text: "Home", link: "/" },
					{ id: "nav-item-2", text: "Products", link: "/products" },
					{ id: "nav-item-3", text: "About", link: "/about" },
					{ id: "nav-item-4", text: "Contact", link: "/contact" },
				],
				fontWeight: "normal", // Added font weight
				textDecoration: "none", // Added underline option
				fontStyle: "normal", // Added font style
			},
			styles: {
				gap: "24px",
			},
			hideInSizes: ["XS", "S"],
		},
	],
	layout: "standard",
	styles: {
		background: "#ffffff",
		padding: "16px",
		boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
		height: "auto",
	},
	containerType: "full", // Added container type setting
	isSticky: false, // Added sticky setting
	history: [],
	currentHistoryIndex: -1,
};

// Reducer function
const headerReducer = (
	state: HeaderState,
	action: HeaderAction
): HeaderState => {
	let newState: HeaderState;

	switch (action.type) {
		case "UPDATE_COMPONENT":
			newState = {
				...state,
				components: state.components.map((comp) =>
					comp.id === action.payload.id
						? { ...comp, ...action.payload.updates }
						: comp
				),
			};
			break;

		case "ADD_COMPONENT":
			newState = {
				...state,
				components: [...state.components, action.payload],
			};
			break;

		case "REMOVE_COMPONENT":
			newState = {
				...state,
				components: state.components.filter(
					(comp) => comp.id !== action.payload
				),
			};
			break;

		case "UPDATE_LAYOUT":
			newState = {
				...state,
				layout: action.payload as any,
			};
			break;

		case "UPDATE_HEADER_STYLES":
			newState = {
				...state,
				styles: {
					...state.styles,
					...action.payload,
				},
			};
			break;

		case "UPDATE_HEADER_SETTINGS":
			newState = {
				...state,
				...action.payload,
			};
			break;

		case "SET_HEADER_STATE":
			newState = {
				...state,
				...action.payload,
			};
			break;

		case "UNDO":
			if (state.currentHistoryIndex > 0) {
				return {
					...state.history[state.currentHistoryIndex - 1],
					history: state.history,
					currentHistoryIndex: state.currentHistoryIndex - 1,
				};
			}
			return state;

		case "REDO":
			if (state.currentHistoryIndex < state.history.length - 1) {
				return {
					...state.history[state.currentHistoryIndex + 1],
					history: state.history,
					currentHistoryIndex: state.currentHistoryIndex + 1,
				};
			}
			return state;

		case "RECORD_HISTORY":
			const newHistory = [
				...state.history.slice(0, state.currentHistoryIndex + 1),
				action.payload,
			];
			return {
				...state,
				history: newHistory,
				currentHistoryIndex: newHistory.length - 1,
			};

		default:
			return state;
	}

	return newState;
};

export function useHeaderState() {
	const [state, dispatch] = useReducer(headerReducer, initialHeaderState);

	// Record history when state changes (debounced to avoid too many history entries)
	useEffect(() => {
		const timer = setTimeout(() => {
			// Don't record history for UNDO/REDO actions
			if (
				state.history.length === 0 ||
				JSON.stringify(state) !==
					JSON.stringify(state.history[state.currentHistoryIndex])
			) {
				const stateToSave = {
					...state,
					history: [],
					currentHistoryIndex: -1,
				};
				dispatch({ type: "RECORD_HISTORY", payload: stateToSave });
			}
		}, 500);

		return () => clearTimeout(timer);
	}, [state.components, state.layout, state.styles]);

	return { state, dispatch };
}
